import type { StytchAPIError } from '@stytch/vanilla-js';
import { router } from 'expo-router';
import { useCallback } from 'react';
import { isWeb } from 'smoothie';

import { captureException, captureMessage, reset } from '../../logger/Logger';
import {
    isUserFirstSession,
    removeDeviceToken,
    removeUserToken,
} from '../helpers';
import { type LogoutType } from '../types/Auth';
import { useStytch } from './useAuth';
import { useLogoutMessage } from './useLogoutMessage';

export const useLogout = () => {
    const stytch = useStytch();
    const showLogoutMessage = useLogoutMessage();

    const logOut = useCallback(async (logoutType?: LogoutType) => {
        // Revoke Stytch session.
        try {
            // For native, showing the logout message works better here vs after for web
            !isWeb && showLogoutMessage(logoutType);
            await stytch.session.revoke();
            captureMessage(
                `User session revoked via logOut; LogoutType: ${logoutType}`,
            );
        } catch (e) {
            if ((e as StytchAPIError).status_code === 401) {
                // do nothing here because this exception happens when trying to revoke a session for a user that is already logged out
            } else {
                captureException(e);
            }
        }

        // If it was the user's first session (post-signup), clear device token.
        if (isUserFirstSession()) {
            removeDeviceToken();
        }

        // Remove the user token
        removeUserToken();

        // Reset any loggers or event trackers.
        reset();

        // ensures we don't navigate back to whatever page the member was on after they log back in
        // this should be a route that does not require authentication
        router.navigate('/sign-in');

        // For web, showing the logout message here works better vs higher for native
        if (isWeb) {
            // wrap function to prevent the page transition from affecting the message
            setTimeout(() => {
                showLogoutMessage(logoutType);
            }, 100);
        }
    }, []);

    return { logOut };
};
